import { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axiosPrivate from 'config/axiosPrivate';
import { getTextFromHtml } from 'utils/smartContent/smartContentGenerationUtils';

export const NAVIGATING_TO_TRANSLATION_IA_KEY = 'isNavigatingToTranslationIA';

export const isTranslationLoadingContent = (html) => {
  if (!html || typeof html !== 'string') return false;
  return html.includes('gifencours.gif') || html.includes('Traduction en cours');
};

export const buildTranslationLoadingHtml = (loadingLabel) => `
  <div style="text-align: center; padding: 40px;">
    <img src="/Images/gifencours.gif" alt="${loadingLabel}" style="width: 200px; height: 200px; margin-bottom: -30px;" />
    <p style="color: #666; font-size: 16px; font-weight: 500;">${loadingLabel}</p>
  </div>
`;

const extractTextId = (data) => {
  if (data == null) return null;
  if (typeof data === 'number' || typeof data === 'string') {
    const value = String(data).trim();
    return value || null;
  }
  const id = data.id ?? data.textId ?? data.text?.id ?? null;
  return id != null ? String(id) : null;
};

export const useSmartTextTranslation = ({
  t,
  refreshCompanyQuotas,
  onCreditsLimit,
  onError,
}) => {
  const history = useHistory();
  const [isTranslationMode, setIsTranslationMode] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState('');
  const [translationTargetLanguage, setTranslationTargetLanguage] = useState(null);
  const [translationAgentId, setTranslationAgentId] = useState('');
  const [isTranslationSaveModalOpen, setIsTranslationSaveModalOpen] = useState(false);
  const [translationSaveModalMode, setTranslationSaveModalMode] = useState('create');
  const [isSavingTranslation, setIsSavingTranslation] = useState(false);
  const [savedTranslationTextId, setSavedTranslationTextId] = useState(null);
  const savedTranslationTextIdRef = useRef(null);

  const setSavedId = useCallback((id) => {
    const normalized = id != null ? String(id) : null;
    savedTranslationTextIdRef.current = normalized;
    setSavedTranslationTextId(normalized);
  }, []);

  const handleStartTranslation = useCallback(async (
    { targetLanguage, agentId },
    {
      editorContent,
      keyword,
      url,
      remainingCredits,
    }
  ) => {
    if (remainingCredits !== null && remainingCredits <= 0) {
      onCreditsLimit?.();
      return false;
    }

    if (!getTextFromHtml(editorContent).trim()) {
      onError?.(t('Le texte source est requis pour la traduction.'));
      return false;
    }

    setIsTranslationMode(true);
    setIsTranslating(true);
    setTranslationTargetLanguage(targetLanguage);
    setTranslationAgentId(agentId || '');
    setSavedId(null);
    setTranslationSaveModalMode('create');
    setTranslatedContent(buildTranslationLoadingHtml(t('Traduction en cours...')));

    try {
      try {
        await refreshCompanyQuotas?.();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      const response = await axiosPrivate.post(
        '/smart-content/smart_content/traduction/generate_text?maxTokens=10000&chatGPTModel=GPT_4_1&temperature=0.3',
        {
          agentId: agentId || '',
          keyword: keyword || '',
          urlPage: url || '',
          originalText: editorContent,
        }
      );

      const translated =
        response.data?.output ??
        response.data?.translatedText ??
        response.data;
      setTranslatedContent(typeof translated === 'string' ? translated : '');

      try {
        await refreshCompanyQuotas?.();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }
    } catch (error) {
      console.error('Erreur lors de la traduction:', error);
      setTranslatedContent(`<p><em>${t('Une erreur est survenue lors de la traduction.')}</em></p>`);
    } finally {
      setIsTranslating(false);
    }

    return true;
  }, [onCreditsLimit, onError, refreshCompanyQuotas, setSavedId, t]);

  const handleSaveTranslation = useCallback(async ({
    editorContent,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    websites,
    websiteId,
  }) => {
    if (isTranslationLoadingContent(translatedContent)) {
      return false;
    }

    if (!getTextFromHtml(translatedContent).trim()) {
      onError?.(t("Veuillez d'abord traduire un texte avant de sauvegarder."));
      return false;
    }

    if (!getTextFromHtml(editorContent).trim()) {
      onError?.(t('Le texte source est requis pour la sauvegarde.'));
      return false;
    }

    setIsSavingTranslation(true);

    try {
      const payload = {
        keyword: keyword || '',
        url: url || '',
        titleTag: titleTag || '',
        metaDescriptionTag: metaDescriptionTag || '',
        actualTextContent: getTextFromHtml(translatedContent),
        initialTextContent: getTextFromHtml(editorContent),
        actualHtmlContent: translatedContent,
        initialHtmlContent: editorContent,
        activeInitialText: true,
        type: 'TRANSLATED',
        numberWords: getTextFromHtml(translatedContent).split(/\s+/).filter((word) => word.length > 0).length,
        maxWords: 0,
        internalLinking: websites || '',
        aiAgentId: translationAgentId || '',
        aiAgentModel: 'GPT_4O',
        websiteId: websiteId || 0,
      };

      const existingId = savedTranslationTextIdRef.current;

      if (existingId) {
        await axiosPrivate.put(
          `/texts/external/unassigned/${existingId}`,
          payload
        );
        setTranslationSaveModalMode('update');
        setIsTranslationSaveModalOpen(true);
      } else {
        const response = await axiosPrivate.post('/texts/external/unassigned', payload);
        const newId = extractTextId(response.data);

        if (newId) {
          setSavedId(newId);
        }

        setTranslationSaveModalMode('create');
        setIsTranslationSaveModalOpen(true);
      }

      try {
        await refreshCompanyQuotas?.();
      } catch (quotaError) {
        console.warn('Erreur lors du rafraîchissement des crédits:', quotaError);
      }

      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la traduction:', error);
      onError?.(
        error?.response?.data?.message
        || error.message
        || t("Erreur lors de l'enregistrement")
      );
      return false;
    } finally {
      setIsSavingTranslation(false);
    }
  }, [
    onError,
    refreshCompanyQuotas,
    setSavedId,
    t,
    translatedContent,
    translationAgentId,
  ]);

  const handleGoToSavedTranslation = useCallback(() => {
    const textId = savedTranslationTextIdRef.current || localStorage.getItem('AdminClient_TextId');
    setIsTranslationSaveModalOpen(false);

    try {
      sessionStorage.setItem(NAVIGATING_TO_TRANSLATION_IA_KEY, 'true');
      if (textId) {
        localStorage.setItem('AdminClient_TextId', String(textId));
      }
    } catch { }

    history.push(textId ? `/AdminClient/CreateTraductionIA/${textId}` : '/AdminClient/CreateTraductionIA');
  }, [history]);

  const handleStayHere = useCallback(() => {
    setIsTranslationSaveModalOpen(false);
  }, []);

  return {
    isTranslationMode,
    isTranslating,
    translatedContent,
    setTranslatedContent,
    translationTargetLanguage,
    translationAgentId,
    isTranslationSaveModalOpen,
    setIsTranslationSaveModalOpen,
    translationSaveModalMode,
    isSavingTranslation,
    savedTranslationTextId,
    handleStartTranslation,
    handleSaveTranslation,
    handleGoToSavedTranslation,
    handleStayHere,
  };
};
